import React from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import TextController from "../../form-fields/TextController";
import DateController from "../../form-fields/DateController";
import IntegerController from "../../form-fields/IntegerController";
import ClientLookupController from "../../form-fields/ClientLookupController";
import { Card, CardContent, CardHeader } from "../../../components/ui/card";
import { Info, ExternalLink } from "lucide-react";

export default function FamilyInformationComponent({
  client,
}: {
  client: ClientType;
}) {
  const { watch } = useFormContext();
  
  // Watch the related_client_id field to conditionally show/hide spouse fields
  const relatedClientId = watch("related_client_id");
  const hasLinkedClient = relatedClientId !== null && relatedClientId !== undefined;

  // Get linked client name and ID from metadata for display
  // IMPORTANT: Only show metadata if the current form value matches the metadata ID
  const linkedClientData = hasLinkedClient && 
    client?.related_client_id_meta?.[0] &&
    client.related_client_id_meta[0].id?.toString() === relatedClientId?.toString()
    ? {
        name: `${client.related_client_id_meta[0].firstName} ${client.related_client_id_meta[0].lastName}`,
        id: client.related_client_id_meta[0].id
      }
    : null;

  return (
    <Card className="mb-8 shadow" id="family-information">
      <CardHeader>
        <h4 className="text-lg font-semibold text-gray-700">
          Family Information
        </h4>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        {/* Show manual family fields only when NO linked client */}
        {!hasLinkedClient && (
          <>
            <div className="my-2">
              <TextController
                name="spousesName"
                label="Spouse's Name"
                defaultValue={client?.spousesName || ""}
                placeholder="Enter spouse's name"
                infoMessage="Please enter your spouse's full name."
                capitalize={true}
              />
            </div>
            <div className="my-2">
              <DateController
                name="spousesDateOfBirth"
                label="Spouse's Date of Birth"
                placeholder="YYYY-MM-DD"
                defaultValue={client?.spousesDateOfBirth || ""}
                infoMessage="Please enter the spouse's date of birth."
                validation={{
                  // required: "Date of birth is required",
                  min: {
                    value: "01/01/1900",
                    message: "Date cannot be before 01/01/1900",
                  },
                  max: {
                    value: "12/31/2023",
                    message: "Date cannot be after 12/31/2023",
                  },
                  noFutureDates: true,
                }}
              />
            </div>
            <div className="my-2">
              <IntegerController
                name="dependents"
                label="Number of Dependents"
                defaultValue={client?.dependents || 0}
                placeholder="Enter number of dependents"
                infoMessage="Please enter the number of dependents."
              />
            </div>
          </>
        )}

        {/* Link to Existing Client (Optional) */}
        <div className="my-2">
          <ClientLookupController
            name="related_client_id"
            label="Link to Existing Client"
            defaultValue={client?.related_client_id || null}
            currentClientId={client?.id}
            relatedClientMeta={client?.related_client_id_meta}
            infoMessage={
              hasLinkedClient
                ? "Spouse and family information is pulled from the linked client record."
                : "Optional: If spouse is already a client, you can link them here instead of entering details manually."
            }
          />
        </div>

        {/* Show info message when client is linked */}
        {hasLinkedClient && linkedClientData && (
          <div className="my-2 col-span-1 md:col-span-2">
            <div className="flex items-start gap-2 p-4 bg-green-40 border-2 border-accent rounded-lg">
              <Info className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
              <div className="text-sm flex-1">
                <p className="font-semibold text-accent">
                  Spouse Linked to Client: {linkedClientData.name}
                </p>
                <div className="flex items-center gap-3 mt-1.5">
                  <p className="text-gray-700">
                    To enter family details manually, clear the linked client selection above.
                  </p>
                  {linkedClientData?.id && (
                    <Link
                      to={`/edit-client/${linkedClientData.id}`}
                      className="flex items-center gap-1.5 text-accent font-medium transition-colors whitespace-nowrap hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Client
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
